import { useState } from 'react';
import { SubmitHandler, useForm, Controller } from 'react-hook-form';
import dynamic from 'next/dynamic';

/* Components */
import { Button, BUTTON_THEMES } from 'ht-styleguide';
import { EmailFieldWithValidation, useExternalEmailCheck } from '@components/UI/EmailFieldWithValidation';

/* Queries & Mutations */
import useSubscriptionSubscribe from '@store/queries/subscription/useSubscriptionsMutation.subscribe';

/* Constants / Utils */
import { YupSchemaResolver } from '@constants/validations';
import { LeadGeneratedTrackingEvent, LEAD_GEN_SOURCE_TYPES } from '@integrations/segment/leadgen';
import { getSetHookFormError } from '@components/UI/EmailFieldWithValidation/EmailFieldWithValidation.hooks';

/* Styles */
import styles from '../footer.module.scss';

/* Dynamic Components */
const FirstServiceDiscountThankYouDynamic = dynamic(
  () => import('@components/Modals/FirstServiceDiscount/FirstServiceDiscountThankYou/FirstServiceDiscountThankYou'),
  { ssr: false },
);

/* Inline Types */
type YupFormProps = { email: string };

const SubscribeForm = () => {
  /* Local State */
  const [dismissedCouponText, setDismissedCouponText] = useState<string | null>(null);

  /* Hooks */
  const {
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    setError,
    control,
  } = useForm<YupFormProps>({
    ...YupSchemaResolver(['email'], 'onChange', {
      email: '',
    }),
  });

  const subscribeMutation = useSubscriptionSubscribe();
  const couponText = subscribeMutation.data?.coupon?.text;
  const visible = Boolean(couponText) && dismissedCouponText !== couponText;

  /* Constants & Methods */
  const subscribeEmail: SubmitHandler<YupFormProps> = ({ email }) => {
    subscribeMutation.mutate(
      {
        email,
        type: 2,
      },
      {
        onSuccess: () => {
          LeadGeneratedTrackingEvent({
            email,
            sourceGroup: LEAD_GEN_SOURCE_TYPES.footer,
          });
        },
      },
    );
  };

  /* ******** EMAILABLE START ********* */
  const { validateEmailWithConfig, emailValidationState, useSuggestion, handleOnChange } = useExternalEmailCheck({
    useValidationService: true,
    setValue,
    setError: getSetHookFormError<keyof YupFormProps>(setError),
    fieldName: 'email',
    onSuccess: () => subscribeEmail({ email: getValues('email') }),
  });
  const allTestsPassed = ({ email }: { email: string }) => {
    validateEmailWithConfig({ email });
  };
  /* ******** EMAILABLE END ********* */

  return (
    <form className={styles.subscriptionForm}>
      {/*  <InputField
        disabled={subscribeMutation.isPending}
        containerClass={styles.input}
        type="text"
        placeholder="Enter your email address"
        error={errors.email?.message || subscribeMutation.data?.errorMessage}
        {...register('email')}
      /> */}

      <Controller
        name="email"
        control={control} // control comes from useForm()
        defaultValue=""
        render={({ field }) => (
          <EmailFieldWithValidation
            {...field}
            containerClass={styles.input}
            type="text"
            onChange={handleOnChange}
            placeholder="Enter your email address"
            error={errors.email?.message || subscribeMutation.data?.errorMessage || ''}
            softWarning={emailValidationState?.did_you_mean || ''}
            useSuggestion={useSuggestion}
          />
        )}
      />
      <Button disabled={subscribeMutation.isPending} theme={BUTTON_THEMES.PRIMARY} className={styles.button} onClick={handleSubmit(allTestsPassed)}>
        Submit
      </Button>
      {/* We can shuck this into the portal if needed #react-portal-placement */}
      {visible && (
        <FirstServiceDiscountThankYouDynamic close={() => setDismissedCouponText(couponText ?? null)} visible={visible} code={couponText!} />
      )}
    </form>
  );
};

export default SubscribeForm;
